import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, Output } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { SystemPreferences, SYSTEM_PREFERENCES } from '../../../../common/system-preferences';
import { AutocompleteAddressDetailsViewModel } from '../../../../models/addresses';
import { ListOption } from '../../../../modules/tcm-form/models/list-option';

@Component({
    selector: 'tcm-address',
    templateUrl: './tcm-address.component.html',
    styleUrls: ['./tcm-address.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    standalone: false
})
export class TcmAddressComponent implements OnChanges {
  hostName: string;

  // REQUIRED FIELDS CONFIGURATION
  @Input() isAddressLine1Required: boolean;
  @Input() isAddressLine2Required: boolean;
  @Input() isCityRequired: boolean;
  @Input() isStateRequired: boolean;
  @Input() isCountryRequired: boolean;
  @Input() isZipRequired: boolean;
  @Input() isPermanentRequired: boolean;

  // COLUMN TITLE KEYS
  @Input() addressLine1TitleKey: string;
  @Input() addressLine2TitleKey: string;
  @Input() cityTitleKey: string;
  @Input() stateTitleKey: string;
  @Input() countryTitleKey: string;
  @Input() zipTitleKey: string;
  @Input() isPrimaryTitleKey: string;
  @Input() latitudeTitleKey: string;
  @Input() longitudeTitleKey: string;
  @Input() googlePlaceIdTitleKey: string;


  // OTHER
  @Input() disabled: boolean;
  @Input() showOnly1Address: boolean;
  @Input() showOnlyCountryState: boolean = false;
  @Input() showIsPrimaryColumn: boolean = true;
  @Input() searchInAllCountries: boolean;
  @Input() countryOptions: ListOption<string>[];
  @Input() statesByCountries: { [key: string]: ListOption<string>[] };

  // Address 1
  @Input() address1Line1: string;
  @Input() address1Line2: string;
  @Input() city1: string;
  @Input() state1: string;
  @Input() country1: string;
  @Input() zip1: string;
  @Input() isAddress1Primary: boolean;

  // Address 2
  @Input() address2Line1: string;
  @Input() address2Line2: string;
  @Input() city2: string;
  @Input() state2: string;
  @Input() country2: string;
  @Input() zip2: string;
  @Input() isAddress2Primary: boolean;

  // Google Place Id
  @Input() enableGooglePlaceId: boolean;
  @Input() latitude: number;
  @Input() longitude: number;
  @Input() googlePlaceId: string;

  // EVENTS
  // Address 1
  @Output() address1Line1Change = new EventEmitter();
  @Output() address1Line2Change = new EventEmitter();
  @Output() city1Change = new EventEmitter();
  @Output() state1Change = new EventEmitter();
  @Output() country1Change = new EventEmitter();
  @Output() zip1Change = new EventEmitter();
  @Output() isAddress1PrimaryChange = new EventEmitter();

  // Address 2
  @Output() address2Line1Change = new EventEmitter();
  @Output() address2Line2Change = new EventEmitter();
  @Output() city2Change = new EventEmitter();
  @Output() state2Change = new EventEmitter();
  @Output() country2Change = new EventEmitter();
  @Output() zip2Change = new EventEmitter();
  @Output() isAddress2PrimaryChange = new EventEmitter();

  // Google Place Id
  @Output() latitudeChange= new EventEmitter();
  @Output() longitudeChange=new EventEmitter();
  @Output() googlePlaceIdChange=new EventEmitter();
  

  state1Options: ListOption<string>[] = [];
  state2Options: ListOption<string>[] = [];

  constructor(@Inject(SYSTEM_PREFERENCES) private readonly systemPreferences: SystemPreferences,
  private el: ElementRef, private cdr: ChangeDetectorRef) {
    this.hostName = el.nativeElement.getAttribute('name');
  }

  ngOnChanges() {    
    this.setState1Options(this.country1);
    this.setState2Options(this.country2);

    this.addState1OptionIfNotExists();
    this.addState2OptionIfNotExists();
  }

  // Address 1
  onAddress1Line1Changed(address1Line1: string) {
    this.address1Line1 = address1Line1;
    this.address1Line1Change.emit(this.address1Line1);

    this.updateIsPrimaryAddress();
  }

  onAddress1Selected(addressModel: AutocompleteAddressDetailsViewModel) {
    // Update country first to set state options
    this.onCountry1Changed(addressModel.country, false);

    let state = null;
    if (this.state1Options && this.state1Options.length) {
      const stateOption = this.state1Options.find(x => x.value == addressModel.state) || this.state1Options.find(x => x.displayName == addressModel.state);
      state = stateOption ? stateOption.value : null;
    }
    this.onState1Changed(state);
    this.clearState1IfNotExists();

    this.onAddress1Line2Changed(null);
    this.onCity1Changed(addressModel.locality);
    this.onZip1Changed(addressModel.postalCode);
  }

  onAddress1Line2Changed(address1Line2: string) {
    this.address1Line2 = address1Line2;
    this.address1Line2Change.emit(this.address1Line2);

    this.updateIsPrimaryAddress();
  }

  onCity1Changed(city1: string) {
    this.city1 = city1;
    this.city1Change.emit(this.city1);

    this.updateIsPrimaryAddress();
  }

  onState1Changed(state1: string) {
    this.state1 = state1;
    this.state1Change.emit(this.state1);

    this.updateIsPrimaryAddress();
  }

  onCountry1Changed(country1: string, clearOtherFieldsIfCountryChanged: boolean = true) {
    let clearOtherFields = false;
    if (this.country1) {
      clearOtherFields = this.country1 != country1;
    } else if (this.state1) {
      clearOtherFields = !this.doesCountryHaveState(country1, this.state1);
    }

    if (clearOtherFieldsIfCountryChanged && clearOtherFields) {
      this.clearAddress1FieldsExceptCountry();
    }

    this.country1 = country1;
    this.country1Change.emit(this.country1);

    this.setState1Options(country1);
    this.clearState1IfNotExists();

    this.updateIsPrimaryAddress();
  }

  onZip1Changed(zip1: string) {
    this.zip1 = zip1;
    this.zip1Change.emit(this.zip1);

    this.updateIsPrimaryAddress();
  }

  onIsAddress1PrimaryChecked(isChecked: boolean) {
    this.isAddress1Primary = isChecked;

    if (this.isAddress1Primary) {
      this.isAddress2Primary = false;
    }

    this.cdr.detectChanges();

    this.updateIsPrimaryAddress();
  }

  // Address 2
  onAddress2Line1Changed(address2Line1: string) {
    this.address2Line1 = address2Line1;
    this.address2Line1Change.emit(this.address2Line1);

    this.updateIsPrimaryAddress();
  }

  onAddress2Selected(addressModel: AutocompleteAddressDetailsViewModel) {
    // Update country first to set state options
    this.onCountry2Changed(addressModel.country, false);

    let state = null;
    if (this.state2Options && this.state2Options.length) {
      const stateOption = this.state2Options.find(x => x.value == addressModel.state) || this.state2Options.find(x => x.displayName == addressModel.state);
      state = stateOption ? stateOption.value : null;
    }
    this.onState2Changed(state);
    this.clearState2IfNotExists();

    this.onAddress2Line2Changed(null);
    this.onCity2Changed(addressModel.locality);
    this.onZip2Changed(addressModel.postalCode);
  }

  onAddress2Line2Changed(address2Line2: string) {
    this.address2Line2 = address2Line2;
    this.address2Line2Change.emit(this.address2Line2);

    this.updateIsPrimaryAddress();
  }

  onCity2Changed(city2: string) {
    this.city2 = city2;
    this.city2Change.emit(this.city2);

    this.updateIsPrimaryAddress();
  }

  onState2Changed(state2: string) {
    this.state2 = state2;
    this.state2Change.emit(this.state2);

    this.updateIsPrimaryAddress();
  }

  onCountry2Changed(country2: string, clearOtherFieldsIfCountryChanged: boolean = true) {
    let clearOtherFields = false;
    if (this.country2) {
      clearOtherFields = this.country2 != country2;
    } else if (this.state2) {
      clearOtherFields = !this.doesCountryHaveState(country2, this.state2);
    }

    if (clearOtherFieldsIfCountryChanged && clearOtherFields) {
      this.clearAddress2FieldsExceptCountry();
    }

    this.country2 = country2;
    this.country2Change.emit(this.country2);

    this.setState2Options(this.country2);
    this.clearState2IfNotExists();

    this.updateIsPrimaryAddress();
  }

  onZip2Changed(zip2: string) {
    this.zip2 = zip2;
    this.zip2Change.emit(this.zip2);

    this.updateIsPrimaryAddress();
  }

  onLatitudeChanged(latitude: number) {    
    this.latitude = latitude;
    this.latitudeChange.emit(this.latitude);

  }

  onLongitudeChanged(longitude: number) {    
    this.longitude = longitude;
    this.longitudeChange.emit(this.longitude);

  }

  onGooglePlaceIdChanged(googlePlaceId: string) {    
    this.googlePlaceId = googlePlaceId;
    this.googlePlaceIdChange.emit(this.googlePlaceId);

  }

  onIsAddress2PrimaryChecked(isChecked: boolean) {
    this.isAddress2Primary = isChecked;

    if (this.isAddress2Primary) {
      this.isAddress1Primary = false;
    }

    this.cdr.detectChanges();

    this.updateIsPrimaryAddress();
  }

  updateIsPrimaryAddress() {
    // Uncheck Address 1 IsPrimary if address doesn't exist
    if (!this.checkAddress1Exists()) {
      this.isAddress1Primary = false;
    }

    // Uncheck Address 2 IsPrimary if address doesn't exist
    if (!this.checkAddress2Exists()) {
      this.isAddress2Primary = false;
    }

    // Detect change after potentially edited isAddress1Primary and isAddress2Primary
    this.cdr.detectChanges();

    // If no address checked as primary, autocheck the first *existing* address
    if (!this.isAddress1Primary && !this.isAddress2Primary) {
      if (this.checkAddress1Exists()) {
        this.isAddress1Primary = true;
      } else if (this.checkAddress2Exists()) {
        this.isAddress2Primary = true;
      }
    }

    // Detect change after potentially edited isAddress1Primary or isAddress2Primary
    this.cdr.detectChanges();

    // Raise IsPrimary changed events
    this.isAddress1PrimaryChange.emit(this.isAddress1Primary);
    this.isAddress2PrimaryChange.emit(this.isAddress2Primary);
  }

  private addState1OptionIfNotExists() {
    if (this.state1 && !this.state1Options.some(x => x.value == this.state1)) {
      this.state1Options.push({ value: this.state1, displayName: this.state1 } as ListOption<string>)
    }
  }

  private addState2OptionIfNotExists() {
    if (this.state2 && !this.state2Options.some(x => x.value == this.state2)) {
      this.state2Options.push({ value: this.state2, displayName: this.state2 } as ListOption<string>)
    }
  }

  private clearAddress1FieldsExceptCountry() {
    this.onAddress1Line1Changed(null);
    this.onAddress1Line2Changed(null);
    this.onCity1Changed(null);
    this.onState1Changed(null);
    this.onZip1Changed(null);
  }

  private clearAddress2FieldsExceptCountry() {
    this.onAddress2Line1Changed(null);
    this.onAddress2Line2Changed(null);
    this.onCity2Changed(null);
    this.onState2Changed(null);
    this.onZip2Changed(null);
  }

  private setState1Options(country: string) {
    if (!country) {
      this.state1Options = [];
    }
    else {
      country = country.toLowerCase();
      if (country in this.statesByCountries) {
        this.state1Options = this.statesByCountries[country];
      } else {
        this.state1Options = [];
      }
    }
  }

  private setState2Options(country: string) {
    if (!country) {
      this.state2Options = [];
    }
    else {
      country = country.toLowerCase();
      if (country in this.statesByCountries) {
        this.state2Options = this.statesByCountries[country];
      } else {
        this.state2Options = [];
      }
    }
  }

  private clearState1IfNotExists() {
    if (!this.state1Options.find(x => x.value == this.state1)) {
      this.state1 = null;
      this.state1Change.emit(this.state1);
    }
  }

  private clearState2IfNotExists() {
    if (!this.state2Options.find(x => x.value == this.state2)) {
      this.state2 = null;
      this.state2Change.emit(this.state2);
    }
  }

  private checkAddress1Exists(): boolean {
    return !!(this.address1Line1 || this.address1Line2 || this.city1 || this.state1 || this.country1 || this.zip1);
  }

  private checkAddress2Exists(): boolean {
    return !!(this.address2Line1 || this.address2Line2 || this.city2 || this.state2 || this.country2 || this.zip2);
  }

  private doesCountryHaveState(country: string, state: string) {
    country = country.toLowerCase();
    var stateOptions = this.statesByCountries[country];
    return stateOptions.some(x => x.value == state);
  }
}
